import { cn } from '@/lib/utils';
import {
  ArrowRight,
  Bell,
  CalendarClock,
  Layers,
  Sparkles,
  TriangleAlert,
  X
} from 'lucide-react';
import { useState } from 'react';

import { openRenewDialog, useAccess } from '@/components/access/useAccess';
import { prefillComposer } from '@/components/chat/MessageComposer';

import {
  OPEN_REPORTS_EVENT,
  TOPIC_GUIDE_PULSE_EVENT,
  WelcomeItem,
  WelcomeTopic,
  acknowledgeTopics,
  dayLabel,
  dismissItems,
  openTopicGuide
} from './api';
import { useWelcome } from './useWelcome';

/** More than this is noise, not a brief; the rest sit behind "Show N more". */
const MAX_ITEMS = 3;

const KIND = {
  access: {
    label: 'Subscription',
    Icon: CalendarClock,
    tile: 'bg-[var(--sig-warn-bg)] text-[var(--sig-warn-icon)] border border-[var(--sig-warn-border)]',
    tag: 'text-[var(--sig-warn-icon)]'
  },
  data: {
    label: 'Data status',
    Icon: TriangleAlert,
    tile: 'bg-[var(--sig-warn-bg)] text-[var(--sig-warn-icon)] border border-[var(--sig-warn-border)]',
    tag: 'text-[var(--sig-warn-icon)]'
  },
  due: {
    label: 'Needs attention',
    Icon: CalendarClock,
    tile: 'bg-[var(--sig-tint)] text-[var(--sig-blue)]',
    tag: 'text-[var(--sig-subtle)]'
  },
  news: {
    label: 'New',
    Icon: Layers,
    tile: 'bg-[var(--sig-chip)] text-[var(--sig-blue)] border border-[var(--sig-border-blue)]',
    tag: 'text-[var(--sig-blue)]'
  }
} as const;

const cardClass =
  'w-full rounded-[14px] border border-[var(--sig-border-blue)] bg-[var(--sig-surface)] shadow-[var(--sig-shadow-card)] overflow-hidden text-left';

function pulseHeader() {
  window.dispatchEvent(new CustomEvent(TOPIC_GUIDE_PULSE_EVENT));
}

function runAction(item: WelcomeItem) {
  if (item.action.type === 'topics') openTopicGuide(item.action.topic);
  else if (item.action.type === 'reports')
    window.dispatchEvent(new CustomEvent(OPEN_REPORTS_EVENT));
  else if (item.action.type === 'renew') openRenewDialog();
}

const ACTION_LABEL: Record<WelcomeItem['id'], string> = {
  access: 'Request renewal',
  data_status: 'See data status',
  reports: 'Open my reports',
  new_topics: 'See questions'
};

function CardHead({
  Icon,
  title,
  sub,
  onClose,
  closeLabel
}: {
  Icon: typeof Sparkles;
  title: string;
  sub: string;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div className="flex items-start gap-3 px-4 pt-4 pb-1">
      <span className="grid place-items-center size-[34px] shrink-0 rounded-[9px] bg-[var(--sig-tint)] text-[var(--sig-blue)]">
        <Icon className="size-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-semibold text-[var(--sig-ink)] [text-wrap:balance]">
          {title}
        </h2>
        <p className="text-[13px] text-[var(--sig-muted)] mt-0.5">{sub}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="grid place-items-center size-8 rounded-lg text-[var(--sig-muted)] hover:bg-[var(--sig-panel)] hover:text-[var(--sig-ink)]"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

function TopicsCard({ topics }: { topics: WelcomeTopic[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const current = topics.find((t) => t.id === open);
  const close = () => {
    acknowledgeTopics();
    pulseHeader();
  };

  return (
    <section className={cardClass} aria-labelledby="welcome-topics-title">
      <CardHead
        Icon={Sparkles}
        title="Here is what you can ask about"
        sub="These are the parts of your ERP data you have access to. Pick one to see example questions."
        onClose={close}
        closeLabel="Dismiss"
      />
      <div className="flex flex-wrap gap-2 px-4 pt-3 pb-1" role="list">
        {topics.map((t) => (
          <button
            key={t.id}
            type="button"
            role="listitem"
            aria-expanded={open === t.id}
            onClick={() => setOpen(open === t.id ? null : t.id)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-[13px] font-medium',
              open === t.id
                ? 'border-[var(--sig-blue)] bg-[var(--sig-tint)] text-[var(--sig-blue-dark)]'
                : 'border-[var(--sig-border-strong)] bg-[var(--sig-panel)] text-[var(--sig-ink)] hover:border-[var(--sig-blue)] hover:bg-[var(--sig-chip)]'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {current ? (
        <div className="mx-4 mt-2 grid gap-1.5 rounded-[10px] border border-[var(--sig-border)] bg-[var(--sig-panel)] px-3 py-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--sig-subtle)]">
            {current.label}
          </span>
          {current.summary ? (
            <p className="text-[13px] text-[var(--sig-body)]">
              {current.summary}
            </p>
          ) : null}
          {current.examples.map((q) => (
            <ExampleButton key={q} question={q} />
          ))}
        </div>
      ) : null}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2.5 border-t border-[var(--sig-border)] px-4 py-3">
        <span className="text-[12.5px] text-[var(--sig-muted)]">
          You can reopen this anytime from <b>What can I ask?</b>
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => openTopicGuide(open ?? undefined)}
            className="rounded-lg border border-[var(--sig-border-strong)] bg-[var(--sig-surface)] px-3.5 py-1.5 text-[13px] font-medium text-[var(--sig-ink)] hover:bg-[var(--sig-panel)]"
          >
            See all topics
          </button>
          <button
            type="button"
            onClick={close}
            className="rounded-lg bg-[var(--sig-blue)] px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-[var(--sig-blue-dark)]"
          >
            Got it
          </button>
        </div>
      </div>
    </section>
  );
}

export function ExampleButton({
  question,
  onPick
}: {
  question: string;
  onPick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onPick?.();
        // Typed into the composer, not sent: a suggestion the reader can edit.
        prefillComposer(question);
      }}
      className="group -mx-2 flex items-start gap-2 rounded-[7px] px-2 py-1.5 text-left text-[13.5px] text-[var(--sig-ink)] hover:bg-[var(--sig-tint)] hover:text-[var(--sig-blue-dark)]"
    >
      <ArrowRight className="mt-[3px] size-3.5 shrink-0 text-[var(--sig-subtle)] group-hover:text-[var(--sig-blue)]" />
      <span>{question}</span>
    </button>
  );
}

function BriefCard({
  items,
  since
}: {
  items: WelcomeItem[];
  since: string | null;
}) {
  const [all, setAll] = useState(false);
  const { access } = useAccess();
  const label = (item: WelcomeItem) =>
    item.id === 'access' && access?.renewal_requested_at
      ? 'Request sent'
      : ACTION_LABEL[item.id];
  const shown = all ? items : items.slice(0, MAX_ITEMS);
  const hidden = items.length - shown.length;
  const when = dayLabel(since);
  const count = `${items.length} ${items.length === 1 ? 'thing' : 'things'} worth a look`;

  return (
    <section className={cardClass} aria-labelledby="welcome-brief-title">
      <CardHead
        Icon={Bell}
        title="Since your last visit"
        sub={when ? `${when} · ${count}` : count}
        onClose={() => dismissItems(items)}
        closeLabel="Dismiss all"
      />
      <ul className="mt-2.5 grid px-2">
        {shown.map((item) => {
          const k = KIND[item.kind];
          return (
            <li
              key={item.id}
              className="grid grid-cols-[32px_1fr_auto] items-start gap-x-3 gap-y-1 border-t border-[var(--sig-border)] px-2 py-3 first:border-t-0"
            >
              <span
                className={cn(
                  'row-span-2 grid size-8 place-items-center rounded-[9px]',
                  k.tile
                )}
              >
                <k.Icon className="size-4" />
              </span>
              <div className="grid min-w-0 gap-0.5">
                <span
                  className={cn(
                    'text-[10.5px] font-semibold uppercase tracking-[.06em]',
                    k.tag
                  )}
                >
                  {k.label}
                </span>
                <span className="text-[14px] font-semibold text-[var(--sig-ink)] [text-wrap:balance]">
                  {item.title}
                </span>
                <span className="text-[13px] tabular-nums text-[var(--sig-muted)]">
                  {item.detail}
                </span>
              </div>
              <button
                type="button"
                onClick={() => dismissItems([item])}
                aria-label="Dismiss this item"
                title="Dismiss"
                className="grid size-7 place-items-center rounded-lg text-[var(--sig-muted)] hover:bg-[var(--sig-panel)] hover:text-[var(--sig-ink)]"
              >
                <X className="size-3.5" />
              </button>
              <div className="col-start-2 col-end-4 mt-1.5 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => runAction(item)}
                  className="rounded-full border border-[var(--sig-border-blue)] bg-[var(--sig-chip)] px-3 py-1 text-[12.5px] font-medium text-[var(--sig-blue-dark)] hover:border-[var(--sig-blue)]"
                >
                  {label(item)}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="mx-4 pt-2 text-[12.5px] font-medium text-[var(--sig-blue-dark)]"
        >
          Show {hidden} more
        </button>
      ) : null}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2.5 border-t border-[var(--sig-border)] px-4 py-3">
        <span className="text-[12.5px] text-[var(--sig-muted)]">
          Only what changed since your last visit.
        </span>
        <button
          type="button"
          onClick={() => dismissItems(items)}
          className="rounded-lg border border-[var(--sig-border-strong)] bg-[var(--sig-surface)] px-3.5 py-1.5 text-[13px] font-medium text-[var(--sig-ink)] hover:bg-[var(--sig-panel)]"
        >
          Dismiss all
        </button>
      </div>
    </section>
  );
}

/**
 * The one card in the empty chat: the topics on a first visit, a short brief
 * when something changed since the last one, and nothing at all otherwise.
 * Renders nothing while the flag is off or the payload has not arrived.
 */
export default function WelcomeCard({ className }: { className?: string }) {
  const { data } = useWelcome();
  if (!data) return null;

  let body = null;
  if (data.mode === 'topics' && data.topics.length) {
    body = <TopicsCard topics={data.topics} />;
  } else if (data.mode === 'brief' && data.items.length) {
    body = <BriefCard items={data.items} since={data.since} />;
  }
  if (!body) return null;

  return (
    <div id="welcome-card" className={cn('w-full', className)}>
      {body}
    </div>
  );
}
